// File: structural_app/frontend/src/components/BeamFormulae/BeamForm.js
// Purpose: This component renders the input form for configuring beam spans, loads, 
// boundary conditions, cross-section, and material. It receives state and 
// event handlers as props from its parent component (BeamFormulaCalculator.js).

import React, { useState, useMemo, useContext, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Paper,
  Menu,
  Popover,
  Stack,
  Divider,
  ListSubheader,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
// Import custom boundary icons
import { PinnedIcon, FixedIcon, SpringIcon } from './SupportIcons';
import BeamContext from '../../context/BeamContext'; // Import the context

const TENDON_PRESETS_URL = '/api/slab-calculator/tendon-presets/';

// Presentational component for the beam configuration form. Receives state and handlers as props from BeamFormulae.js.
const BeamForm = ({
  onSubmit, // Keep onSubmit for now, will connect to context handler later
}) => {
  // --- Get state and setters from Context ---
  const {
  spans,
  crossSections,
  selectedCrossSectionId,
  selectedSection, // Added selectedSection from context
  selectedType, // Keep track of selectedType filter for UI logic
  formErrors,
    // Direct setters (use cautiously, prefer specific handlers)
    setSelectedCrossSectionId, 
    // Get specific handlers from context
    updateSpanLength,
    updateLoad,
    updateBoundary,
    updateTorsionSupport,
    updateWarpingSupport,
    addLoadToSpan,
    removeLoadFromSpan,
    addNewSpan,
    removeSpanByIndex,
    updateSpringCoefficient,
    updateSpringExtra,
    materialFamily,
    advancedOptions,
    setAdvancedOption,
  } = useContext(BeamContext);
  // --- End Context --- 

  const [tendonPresets, setTendonPresets] = useState([]);
  useEffect(() => {
    axios.get(TENDON_PRESETS_URL).then((r) => setTendonPresets(r.data)).catch(() => {});
  }, []);

  // State for the Add Load menu anchor
  const [addLoadAnchorEl, setAddLoadAnchorEl] = useState(null);
  const [currentSpanIndexForMenu, setCurrentSpanIndexForMenu] = useState(null);
  const [boundaryAnchorEl, setBoundaryAnchorEl] = useState(null);
  const [currentBoundary, setCurrentBoundary] = useState(null);
  const [currentSpanIndex, setCurrentSpanIndex] = useState(null);

  // --- Add Logging --- 
  console.log("BeamForm using context spans:", JSON.parse(JSON.stringify(spans)));
  console.log("BeamForm using context formErrors:", formErrors);
  // Log the type of the handler props
  // console.log("BeamForm received onSpanLengthChange typeof:", typeof onSpanLengthChange); // Removed
  // console.log("BeamForm received onLoadChange typeof:", typeof onLoadChange); // Removed
  // --- End Logging ---

  // --- Add Logging for Re-render Check ---
  console.log("--- BeamForm RENDERED --- Context Spans:", JSON.parse(JSON.stringify(spans)));
  // --- End Logging ---

  const getBoundaryIcon = (type, isActive) => {
    const iconSize = "medium"; // Consistent size reference
    const placeholderSize = 24; // Match approximate size of medium icon button

    switch (type) {
      case 'fixed': return <FixedIcon fontSize={iconSize} />;
      case 'pinned': return <PinnedIcon fontSize={iconSize} />;
      case 'rolling_pinned': return <PinnedIcon fontSize={iconSize} />;
      case 'spring': return <SpringIcon fontSize={iconSize} />;
      case 'free': 
        // Return an empty Box with fixed size as placeholder
        return <Box sx={{ width: placeholderSize, height: placeholderSize }} />;
      default: 
        // Return placeholder by default too, or null if preferred
        return <Box sx={{ width: placeholderSize, height: placeholderSize }} />;
    }
  };

  const getBoundaryNextState = (boundary) => {
    // Updated cycle: fixed -> pinned -> free -> fixed
    switch (boundary) {
      case 'fixed': return 'pinned';
      case 'pinned': return 'free'; 
      case 'free': return 'fixed';
      default: return 'pinned'; // Default back to pinned if unknown
    }
  };

  // --- Handlers for Add Load Menu ---
  const handleAddLoadMenuClick = (event, spanIndex) => {
    setAddLoadAnchorEl(event.currentTarget);
    setCurrentSpanIndexForMenu(spanIndex);
  };

  const handleAddLoadMenuClose = () => {
    setAddLoadAnchorEl(null);
    setCurrentSpanIndexForMenu(null);
  };

  const handleAddLoadTypeSelect = (loadType) => {
    if (currentSpanIndexForMenu !== null) {
        addLoadToSpan(currentSpanIndexForMenu, loadType); // Use context handler
    }
    handleAddLoadMenuClose();
  };

  const handleBoundaryClick = (event, spanIndex, boundary) => {
    setBoundaryAnchorEl(event.currentTarget);
    setCurrentBoundary(boundary);
    setCurrentSpanIndex(spanIndex);
  };

  const handleBoundaryMenuClose = () => {
    setBoundaryAnchorEl(null);
    setCurrentBoundary(null);
    setCurrentSpanIndex(null);
  };

  const handleBoundarySelect = (boundaryType) => {
    if (currentSpanIndex !== null && currentBoundary !== null) {
      updateBoundary(currentSpanIndex, currentBoundary, boundaryType); // Use context handler
    }
    handleBoundaryMenuClose();
  };

  const renderLoadInputs = (spanIndex, load, loadIndex) => {
    // Define labels for different load types
    const loadLabels = {
      point: { magnitude: 'Point Load (kN)', position: 'Position (m)' },
      line: { magnitude: 'Line Load (kN/m)', startPosition: 'Start (m)', endPosition: 'End (m)' },
      moment: { magnitude: 'Moment (kNm)', position: 'Position (m)' }
    };
    const currentLabels = loadLabels[load.type] || {};
    const errorKeyBase = `span-${spanIndex}-load-${loadIndex}`; // Base key for errors

    // Log the specific error values being passed to TextField props
    const startError = !!formErrors[`${errorKeyBase}-startPosition`];
    const startHelperText = formErrors[`${errorKeyBase}-startPosition`] || '';
    const endError = !!formErrors[`${errorKeyBase}-endPosition`];
    const endHelperText = formErrors[`${errorKeyBase}-endPosition`] || '';
    const posError = !!formErrors[`${errorKeyBase}-position`];
    const posHelperText = formErrors[`${errorKeyBase}-position`] || '';
    console.log(`renderLoadInputs (Span ${spanIndex}, Load ${loadIndex}, Type ${load.type}):`,
                { startError, startHelperText, endError, endHelperText, posError, posHelperText });

    return (
      <Grid 
        container 
        spacing={1} 
        alignItems="flex-start" 
        sx={{ mb: 1 }}
      >
        <Grid item xs={12} sm={3} md={2}> {/* Adjust grid size */} 
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={load.type || 'point'}
              onChange={(e) => {
                const newType = e.target.value;
                const defaultValue = newType === 'line' 
                  ? { type: newType, magnitude: '', startPosition: '0', endPosition: '', coordinateSystem: 'global', loadAction: load.loadAction || 'permanent', eccentricityY: '0', eccentricityZ: '0' } 
                  : newType === 'moment'
                  ? { type: newType, magnitude: '', position: '0', loadAction: load.loadAction || 'permanent' }
                  : { type: newType, magnitude: '', position: '0', loadAction: load.loadAction || 'permanent', eccentricityY: '0', eccentricityZ: '0' };
                updateLoad(spanIndex, loadIndex, '_updateObject', defaultValue); // Use context handler
              }}
              label="Type"
            >
              <MenuItem value="point">Point</MenuItem>
              <MenuItem value="line">Line</MenuItem>
              <MenuItem value="moment">Moment</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        {advancedOptions.loadsBasis === 'characteristic' && (
          <Grid item xs={12} sm={5} md={3}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Load for ULS</InputLabel>
              <Select
                value={load.loadAction || 'permanent'}
                onChange={(e) => updateLoad(spanIndex, loadIndex, 'loadAction', e.target.value)}
                label="Load for ULS"
              >
                <MenuItem value="permanent">Permanent (γ=1.35)</MenuItem>
                <MenuItem value="variable">Variable (γ=1.50)</MenuItem>
                <MenuItem value="favourable_permanent">Favourable permanent (γ=1.0)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}> {/* Adjust grid size */} 
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            name="magnitude"
            label={advancedOptions.loadsBasis === 'characteristic' ? 'Char. magnitude' : 'Magnitude'}
            value={load.magnitude || ''}
            onChange={(e) => {
              updateLoad(spanIndex, loadIndex, 'magnitude', e.target.value); // Use context handler
            }}
            inputProps={{ step: "any", type: "number" }}
            required
          />
        </Grid>

        {load.type === 'line' ? (
          <>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                name="startPosition"
                label={currentLabels.startPosition || 'Start (m)'}
                value={load.startPosition || ''}
                onChange={(e) => {
                  updateLoad(spanIndex, loadIndex, 'startPosition', e.target.value); // Use context handler
                }}
                inputProps={{ step: "any", type: "number", min: "0" }}
                placeholder="0"
                error={startError}
                helperText={startHelperText || (load.coordinateSystem === 'local' ? 'Along this span' : 'From left end of beam')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                name="endPosition"
                label={currentLabels.endPosition || 'End (m)'}
                value={load.endPosition || ''}
                onChange={(e) => {
                  updateLoad(spanIndex, loadIndex, 'endPosition', e.target.value); // Use context handler
                }}
                inputProps={{ step: "any", type: "number", min: "0" }}
                placeholder={load.coordinateSystem === 'local' ? 'Span end' : 'Beam end'}
                error={endError}
                helperText={endHelperText || 'Empty = full span (local) or full beam (global)'}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={load.coordinateSystem === 'local'}
                    onChange={(e) => {
                      const newCoordSystem = e.target.checked ? 'local' : 'global';
                      updateLoad(spanIndex, loadIndex, 'coordinateSystem', newCoordSystem);
                    }}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Local coordinates</Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">
                {load.coordinateSystem === 'local'
                  ? 'Positions are measured along this span only. Empty start → 0; empty end → span length.'
                  : 'Positions are measured from the left support of the whole beam. Empty start → 0; empty end → total beam length. A global line load may be entered on any span; several loads add together.'}
              </Typography>
            </Grid>
          </>
        ) : (
          <Grid item xs={12} sm={5} md={3}> {/* Position for Point/Moment - Adjust grid size */} 
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              name="position"
              label={currentLabels.position || 'Position (m)'}
              value={load.position || ''}
              onChange={(e) => {
                updateLoad(spanIndex, loadIndex, 'position', e.target.value); // Use context handler
              }}
              inputProps={{ step: "any", type: "number", min: "0" }}
              required
              error={posError}
              helperText={posHelperText}
            />
          </Grid>
        )}
        {(load.type === 'point' || load.type === 'line') && (
          <>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="e_y (mm)"
                value={load.eccentricityY ?? '0'}
                onChange={(e) => updateLoad(spanIndex, loadIndex, 'eccentricityY', e.target.value)}
                inputProps={{ step: 'any', type: 'number' }}
                helperText="Local y (horizontal). Drives torsion / weak-axis bending in the 14-DOF model; major-axis M/V/defl. diagrams stay planar (loads at centroid there)."
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="e_z (mm)"
                value={load.eccentricityZ ?? '0'}
                onChange={(e) => updateLoad(spanIndex, loadIndex, 'eccentricityZ', e.target.value)}
                inputProps={{ step: 'any', type: 'number' }}
                helperText="LTB / 7DOF: destabilising load height uses (e_z − z_s). First-order St. Venant torque from vertical UDL uses e_y only (not e_z)."
              />
            </Grid>
          </>
        )}
        
        <Grid item xs={12} sm={2} md={1} sx={{ textAlign: 'right' }}> {/* Adjust grid size */} 
          <IconButton 
            aria-label="delete load" 
            size="small" 
            onClick={() => removeLoadFromSpan(spanIndex, loadIndex)} // Use context handler
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  // Use crossSections from context
  const sectionOptions = useMemo(() => {
    if (!crossSections || crossSections.length === 0) return [];
    // Filter based on selectedType (also from context)
    const filtered = selectedType === 'all' 
      ? crossSections 
      : crossSections.filter(s => s.type === selectedType);
    // Already sorted by fetch/provider ideally, but can re-sort if needed
    return filtered.map(s => ({ value: s.id, label: s.name }));
  }, [crossSections, selectedType]);

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Beam Configuration
      </Typography>
      
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}> 
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Cross Section</InputLabel>
            <Select
              value={selectedCrossSectionId || ''}
              onChange={(e) => setSelectedCrossSectionId(e.target.value)}
              label="Cross Section"
              disabled={!sectionOptions || sectionOptions.length === 0}
            >
              {sectionOptions.map((section) => (
                <MenuItem key={section.value} value={section.value}>
                  {section.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Accordion
        expanded={!!advancedOptions.expanded}
        onChange={() => setAdvancedOption('expanded', !advancedOptions.expanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Advanced options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {materialFamily === 'steel' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Steel grade (EC3)</InputLabel>
                    <Select
                      label="Steel grade (EC3)"
                      value={advancedOptions.steelGrade}
                      onChange={(e) => setAdvancedOption('steelGrade', e.target.value)}
                    >
                      <MenuItem value="S235">S235</MenuItem>
                      <MenuItem value="S275">S275</MenuItem>
                      <MenuItem value="S355">S355</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="E override (GPa), optional"
                    value={advancedOptions.steelEOverrideGpa}
                    onChange={(e) => setAdvancedOption('steelEOverrideGpa', e.target.value)}
                  />
                </Grid>
                {advancedOptions.enableEc3Checks && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Unbraced length (m), optional"
                        value={advancedOptions.unbracedLengthM}
                        onChange={(e) => setAdvancedOption('unbracedLengthM', e.target.value)}
                        helperText="Per span: semicolons (e.g. 2.5;3). Blank → infer from M diagram and torsion supports at nodes."
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="LTB effective length (m), optional"
                        value={advancedOptions.ltbEffectiveLengthM || ''}
                        onChange={(e) => setAdvancedOption('ltbEffectiveLengthM', e.target.value)}
                        helperText="Optional semicolon-separated per span. Overrides unbraced length for M_cr."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Axial compression N_Ed (kN)"
                        value={advancedOptions.N_Ed_kN || ''}
                        onChange={(e) => setAdvancedOption('N_Ed_kN', e.target.value)}
                        helperText="Compression positive. Also feeds 7-DOF LTB axial N."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Buckling length L_cr,y (m)"
                        value={advancedOptions.L_cr_y_m || ''}
                        onChange={(e) => setAdvancedOption('L_cr_y_m', e.target.value)}
                        helperText="Defaults to LTB/unbraced length."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Buckling length L_cr,z (m)"
                        value={advancedOptions.L_cr_z_m || ''}
                        onChange={(e) => setAdvancedOption('L_cr_z_m', e.target.value)}
                        helperText="Defaults to LTB length or total span."
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Deflection limit span/L (SLS)"
                    value={advancedOptions.deflectionLimitRatio}
                    onChange={(e) => setAdvancedOption('deflectionLimitRatio', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ψ₂ (for characteristic mode)"
                    value={advancedOptions.psi2}
                    onChange={(e) => setAdvancedOption('psi2', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!advancedOptions.enableEc3Checks}
                        onChange={(e) => setAdvancedOption('enableEc3Checks', e.target.checked)}
                      />
                    }
                    label="Enable EC3 design checks"
                  />
                </Grid>
              </>
            )}
            {materialFamily === 'concrete' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quasi-permanent factor ψ2 (SLS)"
                    value={advancedOptions.psi2}
                    onChange={(e) => setAdvancedOption('psi2', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Concrete section</InputLabel>
                    <Select
                      label="Concrete section"
                      value={advancedOptions.concreteSectionShape || 'rectangular'}
                      onChange={(e) => setAdvancedOption('concreteSectionShape', e.target.value)}
                    >
                      <MenuItem value="rectangular">Rectangular b×h</MenuItem>
                      <MenuItem value="inverted_t">Inverted-T (top flange)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {(advancedOptions.concreteSectionShape || 'rectangular') === 'rectangular' ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Width b (m)"
                        value={advancedOptions.concreteWidthM}
                        onChange={(e) => setAdvancedOption('concreteWidthM', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Height h (m)"
                        value={advancedOptions.concreteHeightM}
                        onChange={(e) => setAdvancedOption('concreteHeightM', e.target.value)}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Flange width b_f (m)"
                        value={advancedOptions.invertedTbfM}
                        onChange={(e) => setAdvancedOption('invertedTbfM', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Flange thickness h_f (m)"
                        value={advancedOptions.invertedThfM}
                        onChange={(e) => setAdvancedOption('invertedThfM', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Web width b_w (m)"
                        value={advancedOptions.invertedTbwM}
                        onChange={(e) => setAdvancedOption('invertedTbwM', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Web height h_w (m)"
                        value={advancedOptions.invertedThwM}
                        onChange={(e) => setAdvancedOption('invertedThwM', e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Concrete</InputLabel>
                    <Select
                      label="Concrete"
                      value={advancedOptions.concreteGrade}
                      onChange={(e) => setAdvancedOption('concreteGrade', e.target.value)}
                    >
                      <MenuItem value="C20/25">C20/25</MenuItem>
                      <MenuItem value="C25/30">C25/30</MenuItem>
                      <MenuItem value="C30/37">C30/37</MenuItem>
                      <MenuItem value="C35/45">C35/45</MenuItem>
                      <MenuItem value="C40/50">C40/50</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reinforcement steel</InputLabel>
                    <Select
                      label="Reinforcement steel"
                      value={advancedOptions.concreteSteelGrade}
                      onChange={(e) => setAdvancedOption('concreteSteelGrade', e.target.value)}
                    >
                      <MenuItem value="B500B">B500B</MenuItem>
                      <MenuItem value="B500C">B500C</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cover (m)"
                    value={advancedOptions.concreteCoverM}
                    onChange={(e) => setAdvancedOption('concreteCoverM', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bar Ø (mm)"
                    value={advancedOptions.concreteBarDiamMm}
                    onChange={(e) => setAdvancedOption('concreteBarDiamMm', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Bar count"
                    value={advancedOptions.concreteBarCount}
                    onChange={(e) => setAdvancedOption('concreteBarCount', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Prestress type</InputLabel>
                    <Select
                      label="Prestress type"
                      value={advancedOptions.concretePrestressType || 'none'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAdvancedOption('concretePrestressType', v);
                        if (v === 'none') {
                          setAdvancedOption('prestressNTendons', 0);
                        }
                      }}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="pretensioned">Pretensioned (straight strands)</MenuItem>
                      <MenuItem value="post_tensioned">Post-tensioned / bonded</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {advancedOptions.concretePrestressType !== 'none' && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Tendon preset</InputLabel>
                        <Select
                          label="Tendon preset"
                          value={advancedOptions.prestressTendonPreset || '12.7-1860'}
                          onChange={(e) => setAdvancedOption('prestressTendonPreset', e.target.value)}
                        >
                          {(tendonPresets.length ? tendonPresets : [{ id: '12.7-1860', diameter_mm: 12.7, fpk_mpa: 1860 }]).map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.id} (φ{t.diameter_mm} mm, fpk={t.fpk_mpa})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Number of tendons"
                        type="number"
                        inputProps={{ min: 0, max: 500, step: 1 }}
                        value={advancedOptions.prestressNTendons ?? 0}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setAdvancedOption('prestressNTendons', Number.isNaN(n) ? 0 : Math.min(500, Math.max(0, n)));
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tension σ_pe (MPa)"
                        value={advancedOptions.prestressTensionMpa ?? 1000}
                        onChange={(e) => setAdvancedOption('prestressTensionMpa', e.target.value)}
                        helperText="Effective stress after losses; clamped vs fp0,1k / fpk on server."
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Tendon cover (mm)"
                        type="number"
                        inputProps={{ min: 0, max: 200, step: 1 }}
                        value={advancedOptions.prestressTendonCoverMm ?? 30}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          setAdvancedOption(
                            'prestressTendonCoverMm',
                            Number.isNaN(n) ? 30 : Math.min(200, Math.max(0, n)),
                          );
                        }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Analysis</InputLabel>
                    <Select
                      label="Analysis"
                      value={advancedOptions.analysisType}
                      onChange={(e) => setAdvancedOption('analysisType', e.target.value)}
                    >
                      <MenuItem value="linear">Linear</MenuItem>
                      <MenuItem value="nonlinear">Nonlinear (M-kappa)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Exposure</InputLabel>
                    <Select
                      label="Exposure"
                      value={advancedOptions.exposureClass}
                      onChange={(e) => setAdvancedOption('exposureClass', e.target.value)}
                    >
                      <MenuItem value="XC1">XC1</MenuItem>
                      <MenuItem value="XC2">XC2</MenuItem>
                      <MenuItem value="XC3">XC3</MenuItem>
                      <MenuItem value="XD1">XD1</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>EC2 national annex</InputLabel>
                    <Select
                      label="EC2 national annex"
                      value={advancedOptions.eurocodeNationalAnnex || 'finland'}
                      onChange={(e) => setAdvancedOption('eurocodeNationalAnnex', e.target.value)}
                    >
                      <MenuItem value="finland">
                        Finland (α<sub>cc</sub>=0.85)
                      </MenuItem>
                      <MenuItem value="nl">
                        Netherlands (α<sub>cc</sub>=1.0)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!advancedOptions.concreteAutoRebar}
                        onChange={(e) => setAdvancedOption('concreteAutoRebar', e.target.checked)}
                      />
                    }
                    label="Auto-size reinforcement from ULS moment (linear FEA only; provisional 2×Ø16 mm until sized)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={advancedOptions.enableEc2Checks !== false}
                        onChange={(e) => setAdvancedOption('enableEc2Checks', e.target.checked)}
                      />
                    }
                    label="Enable EC2 design checks"
                  />
                </Grid>
              </>
            )}
            {materialFamily === 'wood' && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Strength class</InputLabel>
                    <Select
                      label="Strength class"
                      value={advancedOptions.timberStrengthClass}
                      onChange={(e) => setAdvancedOption('timberStrengthClass', e.target.value)}
                    >
                      <ListSubheader>Sawn (EN 338)</ListSubheader>
                      <MenuItem value="C14">C14</MenuItem>
                      <MenuItem value="C16">C16</MenuItem>
                      <MenuItem value="C18">C18</MenuItem>
                      <MenuItem value="C22">C22</MenuItem>
                      <MenuItem value="C24">C24</MenuItem>
                      <MenuItem value="C27">C27</MenuItem>
                      <MenuItem value="C30">C30</MenuItem>
                      <MenuItem value="C35">C35</MenuItem>
                      <MenuItem value="C40">C40</MenuItem>
                      <ListSubheader>Glulam</ListSubheader>
                      <MenuItem value="GL24H">GL24H</MenuItem>
                      <MenuItem value="GL24C">GL24C</MenuItem>
                      <MenuItem value="GL28H">GL28H</MenuItem>
                      <MenuItem value="GL28C">GL28C</MenuItem>
                      <MenuItem value="GL32H">GL32H</MenuItem>
                      <MenuItem value="GL32C">GL32C</MenuItem>
                      <MenuItem value="GL36H">GL36H</MenuItem>
                      <MenuItem value="GL36C">GL36C</MenuItem>
                      <ListSubheader>Glulam (legacy labels)</ListSubheader>
                      <MenuItem value="GL24h">GL24h</MenuItem>
                      <MenuItem value="GL28h">GL28h</MenuItem>
                      <MenuItem value="GL32h">GL32h</MenuItem>
                      <ListSubheader>LVL</ListSubheader>
                      <MenuItem value="Kerto-S">Kerto-S</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Service class</InputLabel>
                    <Select
                      label="Service class"
                      value={advancedOptions.timberServiceClass}
                      onChange={(e) => setAdvancedOption('timberServiceClass', e.target.value)}
                    >
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="3">3</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Load duration (EC5)</InputLabel>
                    <Select
                      label="Load duration (EC5)"
                      value={advancedOptions.loadDurationClass}
                      onChange={(e) => setAdvancedOption('loadDurationClass', e.target.value)}
                    >
                      <MenuItem value="permanent">Permanent</MenuItem>
                      <MenuItem value="long_term">Long term</MenuItem>
                      <MenuItem value="medium_term">Medium term</MenuItem>
                      <MenuItem value="short_term">Short term</MenuItem>
                      <MenuItem value="instantaneous">Instantaneous</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {advancedOptions.enableEc5Checks && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="LTB effective length (m), optional"
                      value={advancedOptions.ltbEffectiveLengthM || ''}
                      onChange={(e) => setAdvancedOption('ltbEffectiveLengthM', e.target.value)}
                      helperText="Semicolon-separated per span; max is used for EC5 §6.3.3. Leave blank to use longest span."
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Deflection limit span/L"
                    value={advancedOptions.deflectionLimitRatio}
                    onChange={(e) => setAdvancedOption('deflectionLimitRatio', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ψ₂ (SLS / creep)"
                    value={advancedOptions.psi2}
                    onChange={(e) => setAdvancedOption('psi2', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!advancedOptions.enableEc5Checks}
                        onChange={(e) => setAdvancedOption('enableEc5Checks', e.target.checked)}
                      />
                    }
                    label="Enable EC5 design checks (screening)"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedOptions.loadsBasis === 'characteristic'}
                    onChange={(e) => setAdvancedOption('loadsBasis', e.target.checked ? 'characteristic' : 'design')}
                  />
                }
                label="Characteristic loads — auto ULS/SLS (γ on imposed, ψ₂ for quasi-permanent deflection)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!advancedOptions.selfWeight}
                    onChange={(e) => setAdvancedOption('selfWeight', e.target.checked)}
                  />
                }
                label="Include self-weight"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {spans.map((span, spanIndex) => {
        const spanErrorKey = `span-${spanIndex}-length`;
        const showSteelLtbUi = materialFamily === 'steel' && !!advancedOptions.enableEc3Checks;
        const isLastSpan = spanIndex === spans.length - 1;
        return (
          <Box key={spanIndex} sx={{ mb: 2 }}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">
                    Span {spanIndex + 1}
                  </Typography>
                  {spans.length > 1 && (
                      <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => removeSpanByIndex(spanIndex)} // Use context handler
                          title="Remove Span"
                      >
                          <DeleteIcon fontSize="inherit"/>
                      </IconButton>
                  )}
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    key={`span-length-${spanIndex}`}
                    fullWidth 
                    size="small"
                    variant="outlined"
                    label="Length (m)"
                    value={span.length}
                    onChange={(e) => {
                        updateSpanLength(spanIndex, e.target.value); // Use context handler
                    }}
                    inputProps={{ step: "any", type: "number", min: "0" }}
                    required 
                    error={!!formErrors[spanErrorKey]}
                    helperText={formErrors[spanErrorKey] || ''}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Box display="flex" alignItems="flex-start" justifyContent={spanIndex === 0 ? "space-between" : "flex-end"}>
                    
                      {spanIndex === 0 && (
                      <Stack spacing={0.5} alignItems="center">
                        <Typography variant="caption" color="textSecondary">Left Boundary</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}> 
                          <Tooltip title={`Left Boundary: ${span.leftBoundary}`}>
                            <IconButton
                              onClick={(e) => handleBoundaryClick(e, spanIndex, 'left')}
                              size="small"
                              sx={{ p: 0 }}
                            >
                              {getBoundaryIcon(span.leftBoundary, true)}
                            </IconButton>
                          </Tooltip>
                          {span.leftBoundary === 'spring' && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 280 }}>
                              <TextField
                                label="My"
                                title="Bending spring (kN·m/rad)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={span.leftSpringCoefficient || ''}
                                onChange={(e) => updateSpringCoefficient(spanIndex, 'left', e.target.value)}
                                sx={{ width: '72px' }}
                                inputProps={{ step: 'any' }}
                              />
                              {showSteelLtbUi && (
                                <>
                                  <TextField
                                    label="N"
                                    title="Axial spring (kN/m)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.leftSpringAxial || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'left', 'axial', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                  <TextField
                                    label="Tφ"
                                    title="Torsional spring (kN·m/rad)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.leftSpringTorsion || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'left', 'torsion', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                  <TextField
                                    label="Ψ"
                                    title="Warping spring (kN·m²/rad)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.leftSpringWarping || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'left', 'warping', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                </>
                              )}
                            </Stack>
                          )}
                        </Box>
                        {showSteelLtbUi && (
                          <Stack spacing={0.25} alignItems="center" sx={{ mt: 0.5 }}>
                            <Tooltip title="Restraint against twist φ at this support (lateral–torsional bracing).">
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={span.leftTorsionSupport !== false}
                                    onChange={(e) => updateTorsionSupport(spanIndex, 'left', e.target.checked)}
                                  />
                                }
                                label={
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      Torsion
                                    </Typography>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      support
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Tooltip>
                            <Tooltip title="Warping restraint at this end (shear flow compatible end plate).">
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={span.leftWarpingSupport !== false}
                                    onChange={(e) => updateWarpingSupport(spanIndex, 'left', e.target.checked)}
                                  />
                                }
                                label={
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      Warping
                                    </Typography>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      support
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Tooltip>
                          </Stack>
                        )}
                      </Stack>
                    )}

                    <Stack spacing={0.5} alignItems="center">
                      <Typography variant="caption" color="textSecondary">
                        {spanIndex < spans.length - 1 ? 'Right (interior support)' : 'Right Boundary'}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}> 
                        {span.rightBoundary === 'spring' && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 280 }}>
                              <TextField
                                label="My"
                                title="Bending spring (kN·m/rad)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={span.rightSpringCoefficient || ''}
                                onChange={(e) => updateSpringCoefficient(spanIndex, 'right', e.target.value)}
                                sx={{ width: '72px' }}
                                inputProps={{ step: 'any' }}
                              />
                              {showSteelLtbUi && (
                                <>
                                  <TextField
                                    label="N"
                                    title="Axial spring (kN/m)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.rightSpringAxial || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'right', 'axial', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                  <TextField
                                    label="Tφ"
                                    title="Torsional spring (kN·m/rad)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.rightSpringTorsion || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'right', 'torsion', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                  <TextField
                                    label="Ψ"
                                    title="Warping spring (kN·m²/rad)"
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={span.rightSpringWarping || ''}
                                    onChange={(e) => updateSpringExtra(spanIndex, 'right', 'warping', e.target.value)}
                                    sx={{ width: '64px' }}
                                    inputProps={{ step: 'any' }}
                                  />
                                </>
                              )}
                            </Stack>
                        )}
                        <Tooltip
                          title={
                            spanIndex < spans.length - 1
                              ? `Interior support (end of span ${spanIndex + 1}): ${span.rightBoundary}`
                              : `Right boundary: ${span.rightBoundary}`
                          }
                        >
                          <IconButton
                            onClick={(e) => handleBoundaryClick(e, spanIndex, 'right')}
                            size="small"
                            sx={{ p: 0 }}
                          >
                            {getBoundaryIcon(span.rightBoundary, true)}
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {showSteelLtbUi && (
                        <Stack spacing={0.25} alignItems="center" sx={{ mt: 0.5 }}>
                          <Tooltip title="Restraint against twist φ at this support (lateral–torsional bracing).">
                            <FormControlLabel
                              sx={{ m: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={span.rightTorsionSupport !== false}
                                  onChange={(e) => updateTorsionSupport(spanIndex, 'right', e.target.checked)}
                                />
                              }
                              label={
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="caption" display="block" lineHeight={1.1}>
                                    Torsion
                                  </Typography>
                                  <Typography variant="caption" display="block" lineHeight={1.1}>
                                    support
                                  </Typography>
                                </Box>
                              }
                            />
                          </Tooltip>
                          {isLastSpan && (
                            <Tooltip title="Warping restraint at this end (shear flow compatible end plate).">
                              <FormControlLabel
                                sx={{ m: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={span.rightWarpingSupport !== false}
                                    onChange={(e) => updateWarpingSupport(spanIndex, 'right', e.target.checked)}
                                  />
                                }
                                label={
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      Warping
                                    </Typography>
                                    <Typography variant="caption" display="block" lineHeight={1.1}>
                                      support
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                      Loads on Span {spanIndex + 1}
                  </Typography>
                  {span.loads.map((load, loadIndex) => (
                      <Box key={loadIndex} sx={{ mb: 1 }}>
                          {renderLoadInputs(spanIndex, load, loadIndex)}
                      </Box>
                  ))}
                  
                  <Button
                      aria-controls={`add-load-menu-${spanIndex}`}
                      aria-haspopup="true"
                      onClick={(e) => handleAddLoadMenuClick(e, spanIndex)}
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                  >
                      Add Load
                  </Button>
              </Box>

            </Paper>
          </Box>
        );
      })}
      
      <Popover
        open={Boolean(boundaryAnchorEl)}
        anchorEl={boundaryAnchorEl}
        onClose={handleBoundaryMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              mt: 0.5
            }
          }
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {['fixed', 'pinned', 'rolling_pinned', 'spring', 'free'].map((type, index) => {
            // Removed the conditional rendering, getBoundaryIcon always returns something
            return (
              <React.Fragment key={type}>
                <Tooltip
                  title={
                    type === 'rolling_pinned'
                      ? 'Rolling pinned — transverse restraint, axially free (for compression paths)'
                      : type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleBoundarySelect(type)}
                      sx={type === 'free' ? { '&:hover': { backgroundColor: 'transparent' } } : {}}
                    >
                      {getBoundaryIcon(type)}
                    </IconButton>
                  </span>
                </Tooltip>
                {index < 4 && <Divider orientation="vertical" flexItem />} 
              </React.Fragment>
            );
          })}
        </Stack>
      </Popover>

      {/* --- ADDED BACK: Menu for Add Load --- */}
      <Menu
        id={`add-load-menu-${currentSpanIndexForMenu}`}
        anchorEl={addLoadAnchorEl}
        keepMounted
        open={Boolean(addLoadAnchorEl)}
        onClose={handleAddLoadMenuClose}
      >
        <MenuItem onClick={() => handleAddLoadTypeSelect('point')}>Point Load</MenuItem>
        <MenuItem onClick={() => handleAddLoadTypeSelect('line')}>Line Load</MenuItem>
        <MenuItem onClick={() => handleAddLoadTypeSelect('moment')}>Moment Load</MenuItem>
      </Menu>
      {/* --- End Add Load Menu --- */}

      <Button 
          startIcon={<AddIcon />} 
          onClick={addNewSpan} // Use context handler
          size="small" 
          variant="outlined" 
          sx={{ mt: 2, mb: 2 }}
      >
          Add Span
      </Button>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
              console.log('Calculate button clicked, calling onSubmit...'); 
              onSubmit(); 
          }} 
          disabled={Object.keys(formErrors).length > 0 || !selectedSection}
          fullWidth
        >
          Calculate
        </Button>
      </Box>
    </Paper>
  );
};

export default BeamForm; 